import React, { useState } from 'react';
import {
  PageHeading,
  Pills,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import { DrawerSolicitarMaquininhas } from './DrawerSolicitarMaquininhas';
import './SolicitarMaquininhas.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type RequestStatus = 'Em análise' | 'Aprovada' | 'Enviada' | 'Cancelada';

interface TerminalRequest {
  id: string;
  requestDate: string;
  quantity: number;
  version: string;
  status: RequestStatus;
  estimatedDelivery: string;
  allocatedSellers: number;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const REQUESTS: TerminalRequest[] = [
  {
    id: 'SOL-2026-0041',
    requestDate: '05/04/2026',
    quantity: 12,
    version: 'Sunmi D3 Pro',
    status: 'Em análise',
    estimatedDelivery: '—',
    allocatedSellers: 5,
  },
  {
    id: 'SOL-2026-0038',
    requestDate: '28/03/2026',
    quantity: 5,
    version: 'PAX A920',
    status: 'Aprovada',
    estimatedDelivery: '14/04/2026',
    allocatedSellers: 3,
  },
  {
    id: 'SOL-2026-0031',
    requestDate: '15/03/2026',
    quantity: 8,
    version: 'Sunmi D3 Pro',
    status: 'Enviada',
    estimatedDelivery: '29/03/2026',
    allocatedSellers: 4,
  },
  {
    id: 'SOL-2026-0027',
    requestDate: '02/03/2026',
    quantity: 3,
    version: 'Ingenico Move 5000',
    status: 'Enviada',
    estimatedDelivery: '18/03/2026',
    allocatedSellers: 2,
  },
  {
    id: 'SOL-2026-0019',
    requestDate: '14/02/2026',
    quantity: 20,
    version: 'PAX A920',
    status: 'Enviada',
    estimatedDelivery: '01/03/2026',
    allocatedSellers: 8,
  },
  {
    id: 'SOL-2026-0014',
    requestDate: '28/01/2026',
    quantity: 6,
    version: 'Sunmi D3 Pro',
    status: 'Cancelada',
    estimatedDelivery: '—',
    allocatedSellers: 0,
  },
  {
    id: 'SOL-2025-0093',
    requestDate: '10/12/2025',
    quantity: 10,
    version: 'Ingenico Move 5000',
    status: 'Enviada',
    estimatedDelivery: '22/12/2025',
    allocatedSellers: 5,
  },
];

// ─── Status pill config ───────────────────────────────────────────────────────

type PillState = 'success' | 'info' | 'warning' | 'error' | 'default' | 'accent' | 'alert';

const STATUS_PILL: Record<RequestStatus, PillState> = {
  'Em análise': 'warning',
  'Aprovada':   'info',
  'Enviada':    'success',
  'Cancelada':  'error',
};

// ─── Table columns ────────────────────────────────────────────────────────────

const COLUMNS: ColumnDefinition<TerminalRequest>[] = [
  {
    key: 'id',
    title: 'Solicitação',
    dataIndex: 'id',
    width: 160,
    render: (_v, r) => (
      <Text variant="label-sm">{r.id}</Text>
    ),
  },
  {
    key: 'requestDate',
    title: 'Data',
    dataIndex: 'requestDate',
    width: 130,
  },
  {
    key: 'quantity',
    title: 'Qtd. terminais',
    dataIndex: 'quantity',
    width: 130,
    render: (v) => (
      <Text variant="label-sm">{v as number}</Text>
    ),
  },
  {
    key: 'version',
    title: 'Modelo',
    dataIndex: 'version',
    width: 190,
  },
  {
    key: 'allocatedSellers',
    title: 'Sellers alocados',
    dataIndex: 'allocatedSellers',
    width: 140,
    render: (v) => (v as number) > 0 ? String(v) : '—',
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
    width: 140,
    render: (v) => (
      <Pills
        label={v as string}
        state={STATUS_PILL[v as RequestStatus]}
        size="sm"
      />
    ),
  },
  {
    key: 'estimatedDelivery',
    title: 'Previsão de entrega',
    dataIndex: 'estimatedDelivery',
    width: 170,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function SolicitarMaquininhas() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <PortalPayfacsLayout activeItem="solicitar-maquininhas">
      <main className="sm-content">
        <div className="sm-content-inner">
            <PageHeading
              title="Terminais"
              primaryActionLabel="Solicitar maquininhas"
              onPrimaryAction={() => setDrawerOpen(true)}
            />

            <div className="sm-table-card">
              <Table<TerminalRequest>
                data={REQUESTS}
                columns={COLUMNS}
                rowKey={(r) => r.id}
                size="sm"
                emptyText="Nenhuma solicitação encontrada."
                pagination={{
                  currentPage: 1,
                  totalPages: 1,
                  totalItems: REQUESTS.length,
                  pageSize: 10,
                  pageSizeOptions: [10, 25],
                  onPageChange: () => {},
                  onPageSizeChange: () => {},
                }}
              />
            </div>
          </div>
      </main>
      <DrawerSolicitarMaquininhas
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </PortalPayfacsLayout>
  );
}
